import type { Inert } from "../app/describe.js";
import { printable, type FullText } from "../app/inert.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";

/**
 * DCTA-136 (F10): a line with every flagged string's inert form (describe.ts `Inert`, the bank's `display`) set apart
 * as data — a <bdi class="ref">: isolated, so a bidi control cannot turn the line's own words around, and styled as
 * quoted data (app.css §48), never bold, never a figure. Found in the line, never re-made: the line reads as its words
 * do. DCTA-143: shared by the card and the receipt, so any line either composes around a flagged string sets it apart.
 */
export function refs(text: string, inert: readonly Inert[] | undefined): (Node | string)[] {
  if (inert === undefined || inert.length === 0) return [text];
  const out: (Node | string)[] = [];
  let rest = text;
  for (;;) {
    let at = -1;
    let hit = "";
    for (const i of inert) {
      const k = rest.indexOf(i.display);
      if (k >= 0 && (at < 0 || k < at)) {
        at = k;
        hit = i.display;
      }
    }
    if (at < 0) break;
    if (at > 0) out.push(rest.slice(0, at));
    out.push(el("bdi", { class: "ref" }, [hit]));
    rest = rest.slice(at + hit.length);
  }
  if (rest !== "") out.push(rest);
  return out;
}

/** Ids for the full-text regions: unique in the page, however many cards and sheets it holds. */
let regions = 0;

/** One flagged string, whole: in the bank's quotation marks, each unprinted character as its code point. */
function fullValue(full: string): HTMLElement {
  return el(
    "bdi",
    { class: "full" },
    ["“", ...printable(full).map((p) => ("cp" in p ? el("span", { class: "cp" }, [p.cp]) : p.text)), "”"],
  );
}

/**
 * The bank's sentence beside a flagged string (DCTA-136, Q13 since DCTA-138), as a notice — its first sentence the lead
 * — and, DCTA-143, the disclosure under it: "Show the full text" prints each flagged string the card shows WHOLE, as
 * signed, named by what it is (a <dl>), and "Hide the full text" takes it off again. Collapsed, the region is empty: the
 * whole instruction is on the card only when the customer asks for it. A real <button> with `aria-expanded` and
 * `aria-controls`; focus stays on it. No full texts (an older bank whose display is the whole string): no control.
 */
export function inertNotice(sentence: string, fulls: readonly FullText[] = []): HTMLElement {
  const end = sentence.search(/[.!?](\s|$)/);
  const lead = end < 0 ? sentence : sentence.slice(0, end + 1);
  const rest = end < 0 ? "" : sentence.slice(end + 1);
  const said = el("p", { class: "said" }, [el("b", {}, [lead]), rest]);
  if (fulls.length === 0) return el("div", { class: "notice inert" }, [icon("i-shield"), el("div", {}, [said])]);
  regions += 1;
  const id = `fulltext-${String(regions)}`;
  const region = el("dl", { class: "fulltext", id, hidden: true });
  const toggle = el("button", { class: "raw ft-toggle", type: "button", aria: { expanded: "false", controls: id } }, ["Show the full text"]);
  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") !== "true";
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.textContent = open ? "Hide the full text" : "Show the full text";
    region.replaceChildren(...(open ? fulls.flatMap((f) => [el("dt", {}, [f.label]), el("dd", {}, [fullValue(f.full)])]) : []));
    region.hidden = !open;
  });
  return el("div", { class: "notice inert" }, [icon("i-shield"), el("div", {}, [said, toggle, region])]);
}
